import { API_BASE } from '../config';

// Cache configuration
const CACHE_PREFIX = 'fornbo_cache_';
const BUST_PREFIX = 'fornbo_bust_';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function getCacheKey(endpoint) {
  return `${CACHE_PREFIX}${endpoint.replace(/\//g, '_').replace(/\?/g, '_q_')}`;
}

function getCached(endpoint) {
  try {
    const key = getCacheKey(endpoint);
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const age = Date.now() - timestamp;

    if (age > CACHE_TTL) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

function setCached(endpoint, data) {
  try {
    const key = getCacheKey(endpoint);
    localStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {
    // If storage is full, clear old cache entries
    if (e.name === 'QuotaExceededError') {
      clearOldCache();
      try {
        const key = getCacheKey(endpoint);
        localStorage.setItem(key, JSON.stringify({
          data,
          timestamp: Date.now()
        }));
      } catch (e2) {
        console.warn('Cache save failed after cleanup:', e2);
      }
    }
  }
}

function clearOldCache() {
  try {
    const now = Date.now();
    const keysToDelete = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX)) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (now - cached.timestamp > CACHE_TTL) {
            keysToDelete.push(key);
          }
        } catch {
          keysToDelete.push(key);
        }
      }
    }

    keysToDelete.forEach(key => localStorage.removeItem(key));
  } catch (e) {
    console.warn('Cache cleanup failed:', e);
  }
}

export function invalidateCache(pattern) {
  try {
    const keysToDelete = [];
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_PREFIX) && key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => localStorage.removeItem(key));
    // Mark that the next GET matching this pattern must bypass CDN cache
    localStorage.setItem(BUST_PREFIX + pattern, Date.now().toString());
  } catch (e) {
    console.warn('Cache invalidation failed:', e);
  }
}

function checkAndConsumeBust(endpoint) {
  const cacheKey = getCacheKey(endpoint);
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith(BUST_PREFIX) && cacheKey.includes(k.slice(BUST_PREFIX.length))) {
        localStorage.removeItem(k);
        return true;
      }
    }
  } catch {}
  return false;
}

// Generic fetch wrapper
async function fetchAPI(endpoint, options = {}) {
  const method = options.method || 'GET';
  const isGet = method === 'GET';

  // Check cache first for GET requests
  if (isGet) {
    const cached = getCached(endpoint);
    if (cached) {
      return cached;
    }
  }

  // After an explicit invalidation, append timestamp to bypass Netlify CDN cache
  const bust = isGet && checkAndConsumeBust(endpoint);
  const url = bust
    ? `${API_BASE}${endpoint}${endpoint.includes('?') ? '&' : '?'}_t=${Date.now()}`
    : `${API_BASE}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
    cache: 'no-store'
  });

  if (response.status === 401) {
    window.location.href = '/styrelsen';
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Request failed');
  }

  // Cache successful GET responses
  if (isGet) {
    setCached(endpoint, data);
  }

  return data;
}

// Blob fetch (for CSV and file downloads - preserves encoding)
async function fetchBlob(endpoint, options = {}) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include'
  });

  if (response.status === 401) {
    window.location.href = '/styrelsen';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const text = await response.text();
    try {
      const data = JSON.parse(text);
      throw new Error(data.message || 'Request failed');
    } catch {
      throw new Error('Request failed');
    }
  }

  return response.blob();
}

// Multipart fetch (for file uploads — no Content-Type header, browser sets boundary)
async function fetchMultipart(endpoint, formData) {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    body: formData
  });
  if (response.status === 401) {
    window.location.href = '/styrelsen';
    throw new Error('Unauthorized');
  }
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Upload failed');
  return data;
}

// Public API methods
export const HomeAPI = {
  get: () => fetchAPI('/home'),
};

export const NewsAPI = {
  getAll: () => fetchAPI('/news'),
  getById: (id) => fetchAPI(`/news/${id}`),
};

export const DocumentsAPI = {
  getAll: () => fetchAPI('/documents'),
  getPaginated: (page = 1, limit = 10, search = '', category = '') => {
    const params = new URLSearchParams();
    if (page > 1) params.append('page', page);
    if (limit !== 10) params.append('limit', limit);
    if (search) params.append('search', search);
    if (category) params.append('category', category);
    const queryString = params.toString();
    return fetchAPI(`/documents${queryString ? '?' + queryString : ''}`);
  },
  // Download file - returns base64 data that frontend converts to download
  download: (id) => fetchAPI(`/documents/${id}/download`),
};

export const BoardAPI = {
  getAll: () => fetchAPI('/board'),
};

export const PagesAPI = {
  getAll: () => fetchAPI('/pages'),
  getPage: (category, slug) => fetchAPI(`/pages/${category}/${slug}`),
};

// Protected API methods (require authentication)
export const ProtectedNewsAPI = {
  getAll: () => fetchAPI('/news'),
  create: (data) => fetchAPI('/news', { method: 'POST', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_news');
    invalidateCache('_home');
    return result;
  }),
  update: (id, data) => fetchAPI(`/news/${id}`, { method: 'PUT', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_news');
    invalidateCache('_home');
    return result;
  }),
  delete: (id) => fetchAPI(`/news/${id}`, { method: 'DELETE' }).then(result => {
    invalidateCache('_news');
    invalidateCache('_home');
    return result;
  }),
};

export const ProtectedDocumentsAPI = {
  getAll: () => fetchAPI('/documents'),
  create: (data) => fetchAPI('/documents', { method: 'POST', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_documents');
    invalidateCache('_home');
    return result;
  }),
  update: (id, data) => fetchAPI(`/documents/${id}`, { method: 'PUT', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_documents');
    invalidateCache('_home');
    return result;
  }),
  delete: (id) => fetchAPI(`/documents/${id}`, { method: 'DELETE' }).then(result => {
    invalidateCache('_documents');
    invalidateCache('_home');
    return result;
  }),
  upload: (formData) => fetchMultipart('/documents/upload', formData).then(result => {
    invalidateCache('_documents');
    invalidateCache('_home');
    return result;
  }),
  replace: (id, formData) => fetchMultipart(`/documents/${id}/replace`, formData).then(result => {
    invalidateCache('_documents');
    invalidateCache('_home');
    return result;
  }),
  download: (id) => fetchAPI(`/documents/${id}/download`),
  syncScan: (bucket) => fetchAPI('/documents/sync-scan', { method: 'POST', body: JSON.stringify({ bucket }) }),
  syncConfirm: (bucket, files) => fetchAPI('/documents/sync-confirm', { method: 'POST', body: JSON.stringify({ bucket, files }) }).then(result => {
    invalidateCache('_documents');
    invalidateCache('_home');
    return result;
  }),
  bulkUpload: (bucket, files) => fetchAPI('/documents/bulk-upload', { method: 'POST', body: JSON.stringify({ bucket, files }) }).then(result => {
    invalidateCache('_documents');
    invalidateCache('_home');
    return result;
  }),
  requestUploadUrl: (data) => fetchAPI('/documents/request-upload', { method: 'POST', body: JSON.stringify(data) }),
  registerDocument: (data) => fetchAPI('/documents/register', { method: 'POST', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_documents');
    invalidateCache('_home');
    return result;
  }),
  requestReplaceUrl: (id, data) => fetchAPI(`/documents/${id}/request-replace`, { method: 'POST', body: JSON.stringify(data) }),
  registerReplacement: (id, data) => fetchAPI(`/documents/${id}/register-replace`, { method: 'POST', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_documents');
    invalidateCache('_home');
    return result;
  }),
  move: (id, is_protected) => fetchAPI(`/documents/${id}/move`, { method: 'POST', body: JSON.stringify({ is_protected }) }).then(result => {
    invalidateCache('_documents');
    invalidateCache('_home');
    return result;
  }),
  bulkUpdate: (ids, updates) => fetchAPI('/documents/bulk-update', { method: 'POST', body: JSON.stringify({ ids, updates }) }).then(result => {
    invalidateCache('_documents');
    invalidateCache('_home');
    return result;
  }),
  generateShareLink: (id) => fetchAPI(`/documents/${id}/share`, { method: 'POST' }),
  getShares: () => fetchAPI('/documents/shares'),
  deleteShare: (id) => fetchAPI(`/documents/shares/${id}`, { method: 'DELETE' }),
};

export const ProtectedBoardAPI = {
  update: (data) => fetchAPI('/board', { method: 'PUT', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_board');
    invalidateCache('_home');
    return result;
  }),
};

// Funktionärer API (public read, admin write)
export const FunktionarerAPI = {
  getAll: () => fetchAPI('/funktionarer'),
};

export const ProtectedFunktionarerAPI = {
  update: (data) => fetchAPI('/funktionarer', { method: 'PUT', body: JSON.stringify({ funktionarer: data }) }).then(result => {
    invalidateCache('_funktionarer');
    invalidateCache('_home');
    return result;
  }),
};

// Contact Entries API (public read, contentCreator+ write)
export const ContactEntriesAPI = {
  getAll: () => fetchAPI('/contact-entries'),
};

export const ProtectedContactEntriesAPI = {
  create: (data) => fetchAPI('/contact-entries', { method: 'POST', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_contact');
    invalidateCache('_home');
    return result;
  }),
  update: (id, data) => fetchAPI(`/contact-entries/${id}`, { method: 'PUT', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_contact');
    invalidateCache('_home');
    return result;
  }),
  delete: (id) => fetchAPI(`/contact-entries/${id}`, { method: 'DELETE' }).then(result => {
    invalidateCache('_contact');
    invalidateCache('_home');
    return result;
  }),
};

export const ProtectedPagesAPI = {
  create: (data) => fetchAPI('/pages', { method: 'POST', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_pages');
    invalidateCache('_home');
    return result;
  }),
  update: (category, slug, data) => fetchAPI(`/pages/${category}/${slug}`, { method: 'PUT', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_pages');
    invalidateCache('_home');
    return result;
  }),
  delete: (category, slug) => fetchAPI(`/pages/${category}/${slug}`, { method: 'DELETE' }).then(result => {
    invalidateCache('_pages');
    invalidateCache('_home');
    return result;
  }),
};

// Categories API (public read, admin write)
export const CategoriesAPI = {
  getAll: () => fetchAPI('/categories'),
};

export const ProtectedCategoriesAPI = {
  create: (data) => fetchAPI('/categories', { method: 'POST', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_categories');
    return result;
  }),
  update: (id, data) => fetchAPI(`/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_categories');
    return result;
  }),
  delete: (id) => fetchAPI(`/categories/${id}`, { method: 'DELETE' }).then(result => {
    invalidateCache('_categories');
    return result;
  }),
};

// Matrikel API (readOnly can view, contentCreator+ can edit)
export const MatrikelAPI = {
  getAll: () => fetchAPI('/matrikel'),
  export: () => fetchBlob('/matrikel/export'),
  exportEmails: () => fetchBlob('/matrikel/export/emails'),
};

export const ProtectedMatrikelAPI = {
  getAll: () => fetchAPI('/matrikel'),
  create: (data) => fetchAPI('/matrikel', { method: 'POST', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_matrikel');
    return result;
  }),
  update: (id, data) => fetchAPI(`/matrikel/${id}`, { method: 'PUT', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_matrikel');
    return result;
  }),
  delete: (id) => fetchAPI(`/matrikel/${id}`, { method: 'DELETE' }).then(result => {
    invalidateCache('_matrikel');
    return result;
  }),
  export: () => fetchBlob('/matrikel/export'),
  exportEmails: () => fetchBlob('/matrikel/export/emails'),
};

// Admin API
export const AdminAPI = {
  getUsers: () => fetchAPI('/auth/users'),
  createUser: (data) => fetchAPI('/auth/users', { method: 'POST', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_users');
    return result;
  }),
  updateUser: (userId, data) => fetchAPI(`/auth/users/${userId}`, { method: 'PUT', body: JSON.stringify(data) }).then(result => {
    invalidateCache('_users');
    return result;
  }),
  deleteUser: (userId) => fetchAPI(`/auth/users/${userId}`, { method: 'DELETE' }).then(result => {
    invalidateCache('_users');
    return result;
  }),
  changePassword: (userId, data) => fetchAPI(`/auth/users/${userId}/password`, { method: 'PUT', body: JSON.stringify(data) }),
};
